import { Pros } from 'src/providers/pros/pros';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDatepicker } from '@angular/material/datepicker';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { debounceTime } from 'rxjs/operators';
import { FlatTreeControl } from '@angular/cdk/tree';
import {
  MatTreeFlattener,
  MatTreeFlatDataSource,
} from '@angular/material/tree';
import { Schedule } from 'src/providers/schedule/schedule';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { AddressModel } from 'src/models/address.model';
import {
  Filter,
  ItemNode,
  FlatNode,
  ChipFilter,
  SelectedFilters,
} from 'src/models/schedule.model';
import { BookService } from 'src/shared/services/book.service';
import { SelectCategoryPage } from 'src/pages/select-category/select-category';
import { BookJobPage } from 'src/pages/booking/book-job/book-job';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { BookJob } from 'src/providers/book-job/book-job';
import { DateTime } from 'luxon';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { SelectPropertyPage } from 'src/pages/select-property/select-property';
import { Util } from 'src/shared/util/util';
@Component({
    templateUrl: 'schedule-weekly-filter.html',
    styleUrls: ['./schedule-weekly-filter.scss'],
    selector: 'tidy-schedule-weekly-filter',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ScheduleWeeklyFilterComponent implements OnInit, OnChanges {

  @Input() isLoading: boolean;
  @Input() loaded: boolean;
  @Input() addresses: AddressModel[];
  @Input() showLoadingSpinner: any;
  @Input() selectedFilters: SelectedFilters;
  @Input() selectedPeriodRange: any;
  @Input() initialAddressFilter: number[] = [];
  @Input() initialFilters: SelectedFilters;
  @Input() showAboutThisPage: boolean;
  @Output() searchChange = new EventEmitter<string>();
  @Output() filter = new EventEmitter<Filter>();
  @Output() goToPreviousPeriod = new EventEmitter<any>();
  @Output() goToNextPeriod = new EventEmitter<any>();
  @Output() changeScheduleViewMode = new EventEmitter<any>();
  @Output() dateRangeChange = new EventEmitter<any>();
  @Output() addOwnerBlock = new EventEmitter<void>();
  @ViewChild('picker') picker: MatDatepicker<Date>;
  private _transformer = (node: ItemNode, level: number) => ({
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      level,
      data: node,
    });
  treeControl = new FlatTreeControl<FlatNode>(
    (node) => node.level,
    (node) => node.expandable
  );
  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );
  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  form: UntypedFormGroup;
  dateRangeForm: UntypedFormGroup;
  chips: ChipFilter[] = [];
  filterNamesReferences = {
    Status: 'status',
    Type: 'type',
    Service: 'service',
    Address: 'address',
    'Property Name': 'propertyName',
    Pro: 'pro',
  };
  scheduleViewModeList = [
    { viewValue: new TranslationPipe().transform('List'), value: 'LIST' },
    { viewValue: new TranslationPipe().transform('Day'), value: 'DAY' },
    { viewValue: new TranslationPipe().transform('7 Days'), value: 'SEVEN_DAYS' },
    { viewValue: new TranslationPipe().transform('Week'), value: 'WEEK_MON_SUN' },
    { viewValue: new TranslationPipe().transform('Month'), value: 'MONTHLY' },
  ];
  pros: any[] = [];
  language: string | null;
  scheduleViewMode: string;
  dateRangeSelectOptions: any[] = [];
  showCustomDateRange = false;
  isRestoringDateRange = false;
  isRentalClient = false;
  boundBookJobSelectAddress = this.bookJobSelectAddress.bind(this);

constructor(
    private bookJobService: BookJob,
    private fb: FormBuilder,
    private navCtrl: CustomNavController,
    private schedule: Schedule,
    private storage: TidyStorage,
    private prosProvider: Pros,
    public windowService: WindowService,
    private rightSidePanelService: RightSidePanelService,
    private util: Util,
    private bookService: BookService,
  ) {
    const storedViewMode = localStorage.getItem('scheduleViewMode') || 'SEVEN_DAYS';
    this.scheduleViewMode = storedViewMode;
    this.form = this.fb.group({
      addresses: ['', Validators.required],
      search: [''],
      scheduleViewMode: [storedViewMode],
      dateRangeOption: ['this_month'],
      startDate: [null],
      endDate: [null]
    });
    this.form.valueChanges
      .pipe(debounceTime(300))
      .subscribe((val) => this.searchChange.emit(val));
  }

  async ngOnInit(): Promise<void> {
    this.language = localStorage.getItem('language');
    this.isRentalClient = localStorage.getItem('isRentalClient') === 'true';

    // Initialize date range form for LIST view
    this.dateRangeForm = this.fb.group({
      start: [null],
      end: [null],
      customStringSelect: ['all_time']
    });

    // Initialize date range options for LIST view
    this.dateRangeSelectOptions = [
      { viewValue: new TranslationPipe().transform('Today'), value: 'today' },
      { viewValue: new TranslationPipe().transform('This Week'), value: 'this_week' },
      { viewValue: new TranslationPipe().transform('This Month'), value: 'this_month' },
      { viewValue: new TranslationPipe().transform('Last Week'), value: 'last_week' },
      { viewValue: new TranslationPipe().transform('Last Month'), value: 'last_month' },
      { viewValue: new TranslationPipe().transform('Custom'), value: 'custom' }
    ];


    await this.getPros();
    this.getTreeData();
    this.scheduleViewMode = await this.storage.retrieve('scheduleViewMode') || 'SEVEN_DAYS';
    this.form.get('scheduleViewMode')?.setValue(this.scheduleViewMode);

    if (this.scheduleViewMode === 'LIST') {
      this.showCustomDateRange = false;
    }
    this.form.get('dateRangeOption')?.setValue('this_month');

    this.restoreFilters();
  }

  private restoreFilters(): void {
    if (this.initialFilters) {
      this.selectedFilters = { ...this.initialFilters };
      this.chips = [];
      this.rebuildChipsFromFilters();
    } else {
      this.selectedFilters = {
        status: [],
        type: [],
        service: [],
        address: this.initialAddressFilter || [],
        propertyName: [],
        pro: [],
      };
    }
  }

  private rebuildChipsFromFilters(): void {
    if (!this.selectedFilters) {
      return;
    }

    const filterNameToDisplayName: Record<string, string> = {
      status: 'Status',
      type: 'Type',
      service: 'Service',
      address: 'Address',
      propertyName: 'Property Name',
      pro: 'Pro',
    };

    Object.keys(this.selectedFilters).forEach((filterKey) => {
      const values = this.selectedFilters[filterKey as keyof SelectedFilters];
      if (!values?.length) {
        return;
      }

      values.forEach((value) => {
        const displayName = this.getDisplayNameForFilterValue(filterKey, value);
        if (displayName) {
          this.chips.push({
            displayParentName: filterNameToDisplayName[filterKey] || filterKey,
            parentName: filterKey,
            name: displayName,
            value,
            icon: this.getChipIcon(filterKey, value),
            color: this.getChipColor(filterKey, value),
          });
        }
      });
    });

    this.updateTree();
  }

  private getDisplayNameForFilterValue(filterKey: string, value: any): string {
    if (filterKey === 'address' || filterKey === 'propertyName') {
      const address = this.addresses?.find((a) => a.id === value);
      return filterKey === 'address' ? address?.address1 : address?.address_name;
    }
    if (filterKey === 'pro') {
      const pro = this.pros?.find((p) => p.id === value);
      return pro ? `${pro.first_name} ${pro.last_name}` : '';
    }
    if (filterKey === 'status') {
      const statusMap: Record<string, string> = {
        scheduled: 'Scheduled',
        in_progress: 'In Progress',
        having_issues: 'Having Issues',
        request_waiting_reply: 'Requested',
        request_failed: 'Not Accepted',
        completed: 'Completed',
        cancelled: 'Cancelled',
        homekeeper_cancelled: 'Pro Cancelled',
      };
      return statusMap[value] || value;
    }
    if (filterKey === 'type') {
      const typeMap: Record<string, string> = { job: 'Job', reservation: 'Reservation' };
      return typeMap[value] || value;
    }
    if (filterKey === 'service') {
      return value.replace(/_/g, ' ');
    }
    return value;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.addresses && this.addresses?.length > 0) {
      this.getTreeData();
      if (this.initialFilters && this.hasAnyFilters(this.initialFilters)) {
        this.restoreFilters();
      } else {
        this.applyInitialAddressFilter();
      }
    }
    if (changes.initialFilters && this.initialFilters && this.addresses?.length > 0) {
      this.restoreFilters();
    }
  }

  private hasAnyFilters(filters: SelectedFilters): boolean {
    return Object.values(filters).some((arr) => arr?.length > 0);
  }

  private applyInitialAddressFilter(): void {
    if (!this.initialAddressFilter?.length || !this.selectedFilters || this.showAboutThisPage) {
      return;
    }
    this.initialAddressFilter.forEach(addressId => {
      const foundAddress = this.addresses.find(addr => addr.id === addressId);
      const existingChip = this.chips.find(c => c.parentName === 'address' && c.value === addressId);
      if (foundAddress && !existingChip) {
        const chip: ChipFilter = {
          displayParentName: 'Address',
          parentName: 'address',
          name: foundAddress.address1,
          value: addressId,
          icon: 'home',
          color: foundAddress.color || '#1E2125',
        };
        this.chips.push(chip);
        if (!this.selectedFilters.address?.includes(addressId)) {
          this.selectedFilters.address?.push(addressId);
        }
      }
    });
    this.updateTree();
    this.filter.emit({
      selectedFilters: this.selectedFilters,
      lastSelectedFilter: 'address',
    });
  }

  hasChild = (_: number, node: FlatNode) => node.expandable;

  async getPros(): Promise<void> {
    this.pros = await this.prosProvider.getAllPros();
  }

  getTreeData(): void {
    const treeData = [
      {
        name: 'Status',
        children: [
          {
            name: 'Scheduled',
            value: 'scheduled',
            checked: false
          },
          {
            name: 'In Progress',
            value: 'in_progress',
            checked: false
          },
          {
            name: 'Having Issues',
            value: 'having_issues',
            checked: false
          },
          {
            name: 'Requested',
            value: 'request_waiting_reply',
            checked: false,
          },
          {
            name: 'Not Accepted',
            value: 'request_failed',
            checked: false
          },
          {
            name: 'Completed',
            value: 'completed',
            checked: false
          },
          {
            name: 'Cancelled',
            value: 'cancelled',
            checked: false,
          },
          {
            name: 'Pro Cancelled',
            value: 'homekeeper_cancelled',
            checked: false,
          }
        ],
      },
      {
        name: 'Type',
        children: [
          {
            name: 'Job',
            value: 'job',
            checked: false,
          },
          {
            name: 'Reservation',
            value: 'reservation',
            checked: false,
          },
        ],
      },
      {
        name: 'Service',
        children: this.getServiceTree(),
      },
      {
        name: 'Address',
        children: this.getAddressTree('address1'),
      },
      {
        name: 'Property Name',
        children: this.getAddressTree('address_name'),
      },
      {
        name: 'Pro',
        children: this.getProsTree(this.pros),
      },
    ];
    treeData.forEach((parent, index) => {
      parent.children?.forEach((child) => {
        child.parentId = index;
      });
    });
    const filteredTreeData = treeData.filter((parent) => parent.children?.length > 0);
    this.dataSource.data = filteredTreeData;
  }

  getServiceTree(): ItemNode[] {
    return Object.keys(this.schedule.categoriesImages).map((key) => ({
        name: key.replace(/_/g, ' '),
        value: key,
        image: this.schedule.categoriesImages[key],
        checked: false,
      }));
  }

  getAddressTree(key: string): ItemNode[] {
    return this.addresses
      ?.map((address) => ({
          name: address[key],
          value: key === 'address1' || key === 'address_name' ? address.id : address[key],
          color: address.color || '#1E2125',
          checked: false,
        }))
      .filter((address) => address?.name);
  }

  getProsTree(pros: any[]): ItemNode[] {
    return pros
      .map((pro) => ({
          name: `${pro?.first_name  } ${  pro?.last_name}`,
          value: pro?.id,
          checked: false,
        }))
      .filter((pro) => pro?.name);
  }

  checkNode(node: ItemNode, event: MatCheckboxChange): void {
    node.checked = event.checked;
    this.updateFilters(node, event.checked);
  }

  getNodeVariables(node: ItemNode) {
    const foundParentName = this.dataSource.data[node.data.parentId].name;
    const foundNodeValue = this.dataSource.data[
      node.data.parentId
    ].children.find((child) => child.name === node.name).value;
    const matchedParentName = this.filterNamesReferences[foundParentName];
    return { foundParentName, foundNodeValue, matchedParentName };
  }

  updateFilters(node: ItemNode, isEnabled: boolean): void {
    if (!this.dataSource?.data?.[node?.data?.parentId]?.name) {
      return;
    }
    const { foundParentName, foundNodeValue, matchedParentName } =
      this.getNodeVariables(node);
    const chip: ChipFilter = {
      displayParentName: foundParentName,
      parentName: matchedParentName,
      name: node.name,
      value: foundNodeValue,
      icon: this.getChipIcon(matchedParentName, foundNodeValue),
      color: this.getChipColor(matchedParentName, foundNodeValue),
    };
    if (isEnabled) {
      this.addChip(chip);
    } else {
      this.removeChip(chip, foundParentName);
    }
  }

  private getChipIcon(parentName: string, value: any): string {
    switch (parentName) {
      case 'address':
      case 'propertyName':
        return 'home';
      case 'status':
        return 'flag';
      case 'type':
        return 'category';
      case 'service':
        return this.schedule.categoriesImages[value] || 'build';
      case 'pro':
        return 'person';
      default:
        return '';
    }
  }

  private getChipColor(parentName: string, value: any): string {
    if (parentName === 'address' || parentName === 'propertyName') {
      const address = this.addresses?.find((a) => a.id === value);
      return address?.color || '#1E2125';
    }
    return '';
  }

  addChip(chip: ChipFilter): void {
    if (!this.selectedFilters) {
      return;
    }
    this.selectedFilters[chip.parentName]?.push(chip.value);
    this.chips.push(chip);
    this.filter.emit({
      selectedFilters: this.selectedFilters,
      lastSelectedFilter: chip.parentName,
    });
    this.updateTree();
  }

  updateTree(): void {
    if (!this.selectedFilters) {
      return;
    }
    this.dataSource.data.forEach((parent) => {
      parent.children.forEach((child) => {
        const filterKey = this.filterNamesReferences[parent.name];
        if (
          filterKey && this.selectedFilters[filterKey]?.includes(child.value)
        ) {
          child.checked = true;
        } else {
          child.checked = false;
        }
      });
    });
  }

  removeChip(chip: ChipFilter, parentName: string): void {
    if (!this.selectedFilters) {
      return;
    }
    if (this.selectedFilters[chip.parentName]) {
      this.selectedFilters[chip.parentName] = this.selectedFilters[
        chip.parentName
      ].filter((value) => value !== chip.value);
    }
    this.chips = this.chips.filter(
      (item) => !(item.parentName === chip.parentName && item.value === chip.value)
    );
    this.filter.emit({
      selectedFilters: this.selectedFilters,
      lastSelectedFilter: chip.parentName,
    });
    this.updateTree();
  }

  getNodeChecked(node: ItemNode): boolean {
    if (!this.dataSource?.data?.[node?.data?.parentId]?.name || !this.selectedFilters) {
      return false;
    }
    const { foundNodeValue, matchedParentName } = this.getNodeVariables(node);
    return this.selectedFilters[matchedParentName]?.includes(foundNodeValue) ?? false;
  }

  async bookJobSelectAddress() {
    return this.goToBookJob();
  }

  async goToBookJob() {
    if (!this.addresses?.length) {
      return;
    }
    this.bookJobService.clearBookJobStorage();
    this.storage.save('bookJobBackPage', 'schedule');
    const isRentalClient = localStorage.getItem('isRentalClient') === 'true';
    if (this.addresses.length > 1 && !isRentalClient) {
      const isMobileResolution = !this.windowService.isDesktopRes;
      if (!isMobileResolution) {
        this.storage.save('dialog-right-side-open', true);
        this.rightSidePanelService.openRightSidePanel(SelectPropertyPage);
      } else {
        this.navCtrl.navigateForward('select-property');
      }
    } else {
      const address = this.addresses[0];
      const params = {
        address,
        bookingType: 'add_job',
      };
      await this.bookService.goToBookJob(params);
    }
  }

  goToPreviousPeriodAction() {
    this.goToPreviousPeriod.emit();
  }

  goToNextPeriodAction() {
    this.goToNextPeriod.emit();
  }

  changeScheduleViewModeAction(event: any): void {
    const selectedValue = event?.value || event;

    if (this.scheduleViewMode === 'LIST') {
      const currentDateRangeOption = this.form.get('dateRangeOption')?.value;
      this.storage.save('listViewDateRangeOption', currentDateRangeOption);

      if (this.showCustomDateRange) {
        const startDate = this.form.get('startDate')?.value;
        const endDate = this.form.get('endDate')?.value;
        if (startDate && endDate) {
          this.storage.save('listViewCustomDateRange', {
            startDate: startDate instanceof Date ? startDate.toISOString().split('T')[0] : startDate,
            endDate: endDate instanceof Date ? endDate.toISOString().split('T')[0] : endDate
          });
        }
      }
    }

    if (selectedValue === 'LIST') {
      this.restoreListViewDateRange();
    }

    this.scheduleViewMode = selectedValue;
    this.changeScheduleViewMode.emit(selectedValue);
    this.storage.save('scheduleViewMode', selectedValue);
  }

  private async restoreListViewDateRange(): Promise<void> {
    this.isRestoringDateRange = true;
    const savedDateRangeOption = await this.storage.retrieve('listViewDateRangeOption');
    const savedCustomDateRange = await this.storage.retrieve('listViewCustomDateRange');

    if (savedDateRangeOption) {
      this.form.get('dateRangeOption')?.setValue(savedDateRangeOption);

      if (savedDateRangeOption === 'custom' && savedCustomDateRange) {
        this.showCustomDateRange = true;
        this.form.get('startDate')?.setValue(new Date(savedCustomDateRange.startDate));
        this.form.get('endDate')?.setValue(new Date(savedCustomDateRange.endDate));
        this.emitCustomDateRange();
      } else {
        this.showCustomDateRange = false;
        this.selectStringDateRange(savedDateRangeOption);
      }
    }
    this.isRestoringDateRange = false;
  }


  selectStringDateRange(selectedRange: string): void {
    if (!this.isRestoringDateRange) {
      this.dateRangeChange.emit({ type: 'string', value: selectedRange });
    }
  }

  selectCustomDateRange(event: any): void {
    this.dateRangeChange.emit({ type: 'custom', value: event });
  }

  changeDateRangeOption(event: any): void {
    const selectedValue = event?.value || event;

    if (selectedValue === 'custom') {
      this.showCustomDateRange = true;
      // Initialize with default range: -7 days to +7 days from today
      const today = DateTime.now();
      const defaultStart = today.minus({ days: 7 }).toJSDate();
      const defaultEnd = today.plus({ days: 7 }).toJSDate();

      this.form.get('startDate')?.setValue(defaultStart);
      this.form.get('endDate')?.setValue(defaultEnd);
      this.emitCustomDateRange();
    } else {
      this.showCustomDateRange = false;
      this.selectStringDateRange(selectedValue);
    }
  }

  onStartDateChange(event: any): void {
    const newStartDate = event?.value || event;
    const currentEndDate = this.form.get('endDate')?.value;

    if (newStartDate && currentEndDate &&
        newStartDate instanceof Date && currentEndDate instanceof Date) {

      if (newStartDate.getTime() > currentEndDate.getTime()) {
        this.form.get('endDate')?.setValue(newStartDate);
      }
    }

    // Handle datepicker value change
    this.emitCustomDateRange();
  }

  onEndDateChange(event: any): void {
    const newEndDate = event?.value || event;
    const currentStartDate = this.form.get('startDate')?.value;

    // Check if both dates exist and are valid
    if (newEndDate && currentStartDate &&
        newEndDate instanceof Date && currentStartDate instanceof Date) {

      if (newEndDate.getTime() < currentStartDate.getTime()) {
        this.form.get('startDate')?.setValue(newEndDate);
      }
    }

    // Handle datepicker value change
    this.emitCustomDateRange();
  }

  emitCustomDateRange(): void {
    const startDate = this.form.get('startDate')?.value;
    const endDate = this.form.get('endDate')?.value;

    if (startDate && endDate) {
      // Convert Date objects to ISO strings for consistency
      const startDateISO = startDate instanceof Date ? startDate.toISOString().split('T')[0] : startDate;
      const endDateISO = endDate instanceof Date ? endDate.toISOString().split('T')[0] : endDate;

      if (!this.isRestoringDateRange) {
        this.dateRangeChange.emit({
          type: 'custom',
          value: {
            start: startDateISO,
            end: endDateISO
          }
        });
      }
    }
  }

  goBackToDropdown(): void {
    this.showCustomDateRange = false;
    this.form.get('dateRangeOption')?.setValue('this_month');
    this.form.get('startDate')?.setValue(null);
    this.form.get('endDate')?.setValue(null);
    this.selectStringDateRange('this_month');
  }

  getPeriodRangeText() {
    const format = this.language && this.language !== 'en' ? 'MMMM d' : 'MMM d';
    const startDate = DateTime.fromFormat(this.selectedPeriodRange.startDate, 'yyyy-MM-dd').toFormat(format);
    const endDate = DateTime.fromFormat(this.selectedPeriodRange.endDate, 'yyyy-MM-dd').toFormat(format);

    // Format the display based on view mode
    if (this.scheduleViewMode === 'LIST') {
      // For LIST view, show "Job History"
      return 'Job History';
    } else if (this.scheduleViewMode === 'DAY') {
      // For DAY view, show full date
      const dayDate = DateTime.fromFormat(this.selectedPeriodRange.startDate, 'yyyy-MM-dd');
      const dayFormat = this.language && this.language !== 'en' ? 'EEEE, MMMM d' : 'EEE, MMM d';
      const formattedDate = dayDate.toFormat(dayFormat);
      if (this.language && this.language !== 'en') {
        const parts = formattedDate.split(', ');
        const weekday = new TranslationPipe().transform(parts[0]);
        const month = parts[1].split(' ')[0];
        const day = parts[1].split(' ')[1];
        return `${weekday}, ${new TranslationPipe().transform(month)} ${day}`;
      }
      return formattedDate;
    } else if (this.scheduleViewMode === 'WEEK_MON_SUN') {
      // Format for Mon-Sun week view
      if (this.language && this.language !== 'en') {
        const startMonth = startDate.split(' ')[0];
        const startDay = startDate.split(' ')[1];
        const endMonth = endDate.split(' ')[0];
        const endDay = endDate.split(' ')[1];

        // If start and end are in the same month
        if (startMonth === endMonth) {
          return `${new TranslationPipe().transform(startMonth)} ${startDay}-${endDay}`;
        } else {
          return `${new TranslationPipe().transform(startMonth)} ${startDay} - ${new TranslationPipe().transform(endMonth)} ${endDay}`;
        }
      } else {
        const startObj = DateTime.fromFormat(this.selectedPeriodRange.startDate, 'yyyy-MM-dd');
        const endObj = DateTime.fromFormat(this.selectedPeriodRange.endDate, 'yyyy-MM-dd');

        // If start and end are in the same month
        if (startObj.month === endObj.month) {
          return `${startDate} - ${endDate}`;
          //TODO check with business about logic we want here:
          //return `${startObj.toFormat('MMM')} ${startObj.day}-${endObj.day}, ${startObj.year}`;
        } else {
          return `${startDate} - ${endDate}`;
        }
      }
    }

    if (this.language && this.language !== 'en') {
      const startMonth = startDate.split(' ')[0];
      const startDay = startDate.split(' ')[1];
      const endMonth = endDate.split(' ')[0];
      const endDay = endDate.split(' ')[1];
      return `${new TranslationPipe().transform(startMonth).slice(0,3)} ${startDay} - ${new TranslationPipe().transform(endMonth).slice(0,3)} ${endDay}`;
    } else {
      return `${startDate} - ${endDate}`;
    }
  }

}
